import { Injectable, OnDestroy } from '@angular/core';
import { AndroidFullScreen } from '@ionic-native/android-full-screen/ngx';
import { KeepAwake } from '@capacitor-community/keep-awake';
import { Platform } from '@ionic/angular';
import { KioskMode as CapacitorKioskMode } from 'capacitor-kiosk';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { LogService } from '../baseServices/logging/log.service';
import { ToastLoggingService } from '../baseServices/logging/toast-logging.service';
import { AppUserService } from './app-user.service';
import { RoleClaimService } from './role-claim.service';
import { DeviceInfoService } from './device-info.service';

@Injectable({
  providedIn: 'root',
})
export class KioskModeService implements OnDestroy {
  private readonly canUseKioskModeSubject = new BehaviorSubject<boolean>(false);
  readonly canUseKioskMode$ = this.canUseKioskModeSubject.asObservable();
  get canUseKioskMode(): boolean { return this.canUseKioskModeSubject.value; }

  private readonly isKioskModeEnabledSubject = new BehaviorSubject<boolean>(false);
  readonly isKioskModeEnabled$ = this.isKioskModeEnabledSubject.asObservable();
  get isKioskModeEnabled(): boolean { return this.isKioskModeEnabledSubject.value; }
  
  private kioskModeStateChangeInProgress: boolean = false;
  private backButtonSubscription?: Subscription;
  private readonly destroy$ = new Subject<void>();

  constructor(
    private readonly logService: LogService,
    private readonly toastLoggingService: ToastLoggingService,
    private readonly androidFullScreen: AndroidFullScreen,
    private readonly appUserService: AppUserService,
    private readonly roleClaimService: RoleClaimService,
    private readonly platform: Platform,
    private readonly deviceInfoService: DeviceInfoService,
  ) {
    this.loadSubscriptions();
  }

  ngOnDestroy(): void {
    if (this.backButtonSubscription) this.backButtonSubscription.unsubscribe();
    this.destroy$.next();
    this.destroy$.complete();
  }

  private loadSubscriptions(): void {
    // Subscribe to user changes
    this.appUserService.currentUser$
      .pipe(takeUntil(this.destroy$))
      .subscribe(async () => {
        const hasKioskPermission = this.roleClaimService.checkCurrentUserHasKioskModeClaim();
        this.setCanUseKioskMode(hasKioskPermission);
        
        // Per https://dev.azure.com/TDRRecoveryTrac/RecoveryTrac/_workitems/edit/6048:
        // Kiosk mode is currently enabled at the *account level*.
        // This means that as soon as a user with the kiosk mode claim logs in, they are automatically in kiosk mode.
        //
        // In the future, requirements may change so that "kiosk mode" only applies while a user has opened a form with the kiosk mode option
        // (and allowing any account with permission — not just special kiosk accounts — to enter kiosk mode temporarily).
        //
        // If that change occurs, remove this logic and have the form service call the enable/disable functions directly instead.
        if (hasKioskPermission) {
          await this.enableKioskMode();
        } else {
          await this.disableKioskMode();
        }
      });
  }

  private setCanUseKioskMode(canUse: boolean): void {
    if (this.canUseKioskMode === canUse) return;

    this.canUseKioskModeSubject.next(canUse);
    this.logService.info('KioskMode.CanUseUpdated', { canUse });

    // If permission revoked while kiosk mode is active, disable it
    if (!canUse && this.isKioskModeEnabled) {
      this.disableKioskMode();
    }
  }

  private setIsKioskModeEnabled(isEnabled: boolean): void {
    if (this.isKioskModeEnabled === isEnabled) return;

    this.isKioskModeEnabledSubject.next(isEnabled);
    this.logService.info('KioskMode.StatusChanged', { isEnabled });
  }

  private async enableKioskMode(): Promise<void> {
    if (this.isKioskModeEnabled || this.kioskModeStateChangeInProgress) return;
    this.kioskModeStateChangeInProgress = true;

    try {
      const { value: isKiosk } = await CapacitorKioskMode.isInKioskMode();
      if (!isKiosk) {
        await CapacitorKioskMode.enterKioskMode();
      }

      if (await this.deviceInfoService.getPlatform() === 'android') {
        if (await this.androidFullScreen.isImmersiveModeSupported()) {
          await this.androidFullScreen.immersiveMode();
        }
      }

      if (KeepAwake.isSupported()) {
        await KeepAwake.keepAwake();
      }
      
      this.disableBackButton();

      this.setIsKioskModeEnabled(true);
    } catch (error) {
      this.toastLoggingService.error('KioskMode.EnableError', error, 'An issue was encountered while trying to enable kiosk mode.');
      await this.disableKioskMode();
    } finally {
      this.kioskModeStateChangeInProgress = false;
    }
  }

  private async disableKioskMode(): Promise<void> {
    if (!this.isKioskModeEnabled || this.kioskModeStateChangeInProgress) return;
    this.kioskModeStateChangeInProgress = true;

    try {
      const { value: isKiosk } = await CapacitorKioskMode.isInKioskMode();
      if (isKiosk) {
        await CapacitorKioskMode.exitKioskMode();
      }

      if (await this.deviceInfoService.getPlatform() === 'android') {
        if (await this.androidFullScreen.isImmersiveModeSupported()) {
          await this.androidFullScreen.showSystemUI();
        }
      }

      if (KeepAwake.isSupported()) {
        KeepAwake.allowSleep();
      }
      
      this.enableBackButton();

      this.setIsKioskModeEnabled(false);
    } catch (error) {
      this.toastLoggingService.error('KioskMode.DisableError', error, 'An issue was encountered while trying to disable kiosk mode. Please try refreshing or restarting the application.');
    } finally {
      this.kioskModeStateChangeInProgress = false;
    }
  }

  private enableBackButton(): void {
    if (this.backButtonSubscription) {
      this.backButtonSubscription.unsubscribe();
      this.backButtonSubscription = undefined;
      this.logService.debug('KioskMode.BackButtonRestored');
    }
  }

  private disableBackButton(): void {
    if (this.backButtonSubscription) return;

    this.backButtonSubscription = this.platform.backButton.subscribeWithPriority(9999, () => {
      // Do nothing — swallow the event
      this.logService.debug('KioskMode.BackButtonBlocked');
    });
  }
}
